import { useEffect, useState } from 'react';
import { toast, Toaster } from 'react-hot-toast';
import { getServiceUsageTimeseries } from '../services';
import UserSearchSelect from '../components/UserSearchSelect';
import UsageStatCards from '../components/UsageStatCards';
import UsageChart from '../components/UsageChart';
import CountdownCircle from '../components/CountdownCircle';

const RANGE_OPTIONS = [
  { value: 15, label: 'Trong 15 phút' },
  { value: 60, label: 'Trong 1 giờ' },
  { value: 180, label: 'Trong 3 giờ' },
  { value: 720, label: 'Trong 12 giờ' },
  { value: 1440, label: 'Trong 24 giờ' },
  { value: 4320, label: 'Trong 3 ngày' },
  { value: 10080, label: 'Trong 7 ngày' },
  { value: 43200, label: 'Trong 30 ngày' },
  { value: 129600, label: 'Trong 90 ngày' },
];

const SERVICE_OPTIONS = [
  { value: '', label: 'Tất cả dịch vụ' },
  { value: 'fb_graph', label: 'FB Graph' },
  { value: 'fb_action', label: 'FB Action' },
  { value: 'tiktok', label: 'TikTok' },
];

const FETCH_INTERVAL = 5000;

function getErrorMessage(error) {
  return error.response?.data?.error || error.response?.data?.message || error.message || 'Request failed';
}

export default function ApiStatusPage() {
  const [rangeMinute, setRangeMinute] = useState(15);
  const [serviceCallback, setServiceCallback] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [timeseries, setTimeseries] = useState({});
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [circleKey, setCircleKey] = useState(0);

  useEffect(() => {
    let intervalId;

    async function fetchData() {
      try {
        const userIds = selectedUsers.map((user) => user.id);
        const data = await getServiceUsageTimeseries({
          serviceCallback: serviceCallback || undefined,
          rangeMinute,
          userIds,
        });
        setTimeseries(data.timeseries);
        setSummary(data.summary);
      } catch (error) {
        toast.error(getErrorMessage(error));
      } finally {
        setIsLoading(false);
        if (rangeMinute <= 15) setCircleKey((key) => key + 1);
      }
    }

    fetchData();

    if (rangeMinute <= 15) {
      intervalId = setInterval(fetchData, FETCH_INTERVAL);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeMinute, serviceCallback, selectedUsers]);

  return (
    <section className="space-y-4">
      <Toaster position="top-right" toastOptions={{ duration: 2500 }} />

      <div className="rounded-xl border border-cyan-100 bg-white p-3">
        <div className="grid gap-3 md:grid-cols-3">
          <div className="flex items-center gap-1">
            <select
              value={rangeMinute}
              onChange={(event) => setRangeMinute(Number(event.target.value))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-cyan-400"
            >
              {RANGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {rangeMinute <= 15 && <CountdownCircle key={circleKey} duration={FETCH_INTERVAL} />}
          </div>

          <select
            value={serviceCallback}
            onChange={(event) => setServiceCallback(event.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-cyan-400"
          >
            {SERVICE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <UserSearchSelect value={selectedUsers} onChange={setSelectedUsers} multiple />
        </div>
        <p className="mt-2 text-xs text-slate-500">
          {selectedUsers.length === 0
            ? 'Chưa chọn user cụ thể: đang hiển thị request không gắn với user cụ thể (user_id = 0).'
            : `Đang lọc theo ${selectedUsers.length} user đã chọn.`}
        </p>
      </div>

      {isLoading ? (
        <div className="rounded-2xl border border-cyan-100 bg-white p-6 text-center text-sm text-slate-500">
          Đang tải dữ liệu usage...
        </div>
      ) : (
        <>
          <UsageStatCards summary={summary} />
          <UsageChart timeseries={timeseries} />
        </>
      )}
    </section>
  );
}
